/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useMedia } from 'react-use';
import { Button, Typography, FlipTimer } from 'ck-ui';
import { parseSqlDate } from 'ck-utils/dateTime';
import { formatMoney } from 'ck-utils/price';
import './PromotionBar.global.scss';
import styles from './PromotionBar.module.scss';

const PromotionBar = ({ sectionSettings, slideBlocks, nudgeBlocks }) => {
  const [cart, setCart] = useState(null);
  const isMobile = useMedia('(max-width: 767px)');
  const isCartNudgeActive = sectionSettings.promotion_bar_nudge_enable && nudgeBlocks && nudgeBlocks.length;
  const autoPlayTime = sectionSettings.slider_auto_play_ms;

  useEffect(() => {
    const handleSetCart = e => {
      setCart(e.detail.cart);
    };
    if (isCartNudgeActive) {
      window.addEventListener('afterGetCart', handleSetCart);
      window.ShopifyAPI.getCart();
    } else {
      setCart({});
    }
    return () => {
      if (isCartNudgeActive) {
        window.removeEventListener('afterGetCart', handleSetCart);
      }
    };
  }, [isCartNudgeActive]);

  useEffect(() => {
    if (cart) {
      window.theme.initSlider('.promotion-bar-slider', { enabledMobileCarousel: true, autoPlayTime });
    }
  }, [autoPlayTime, cart]);

  // Dispatch a DOM event for external A/B test platform to subscribe to re-run their experiences
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('afterReactStateUpdate', { detail: { cart } }));
  }, [cart]);

  // early exits; TODO: what if they want no slideblocks but show nudge when it is available
  if (!sectionSettings.countdown_enabled && slideBlocks.length === 0) return null;

  const renderBtn = (btnLocation) => (
    <div className={classnames(
      { [styles.containerRight]: btnLocation === 'right' },
      { [styles.containerLeft]: btnLocation === 'left' },
      'is-hidden-mobile')}
    >
      <Button
        renderAs="a"
        secondary
        noHover
        href={btnLocation === 'left' ? sectionSettings.promotion_button_link_left : sectionSettings.promotion_button_link_right}
        style={{
          borderColor: sectionSettings.promotion_color,
          color: sectionSettings.promotion_color
        }}
      >
        {btnLocation === 'left' ? sectionSettings.promotion_button_text_left : sectionSettings.promotion_button_text_right}
      </Button>
    </div>
  );

  const renderCartNudge = () => {
    if (!isCartNudgeActive) return null;

    // Get Cart total, excluding gift card line items
    const total = cart && cart.items && cart.items.length
      ? cart.items.reduce((acc, item) => (item.gift_card ? acc - item.final_line_price : acc), cart.total_price) / 100
      : 0;
    if (total === 0) return null;

    // Get qualify tier block to render
    const messageBlock = nudgeBlocks.find(
      (block) => total < Number(block.tier_value)
        && total >= Number(block.promotionbar_tier_start)
        && (!block.handle || block.variants?.some((variant) => variant.available)) // remove sold out items' nudge
    );

    // If the total is greater than or equal to the tier value, show the success message
    // if (item.tier_value <= total) {
    //   return (
    //     <div className={styles.callout}>
    //       <Icon name="circle-tick" size="medium" color="success" />
    //
    //       <Typography
    //         color="white"
    //         variant="body-large"
    //         dangerouslySetInnerHTML={{ __html: `${item.promotionbar_after_call_out.replace(/<\/?p>/g, '')} Unlocked` }}
    //       />
    //     </div>
    //   );
    // }

    if (!messageBlock) return null;
    const {
      promotionbar_before_text,
      promotionbar_after_text,
      tier_value
    } = messageBlock;

    return (
      <div className="promotion-slide">
        <div
          className="has-text-centered"
          style={{ color: sectionSettings.promotion_color }}
        >
          <span
            className={styles.text}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: promotionbar_before_text }}
          />
          <Typography color="color-inherit" weight={700}> {formatMoney(Number(tier_value) - total)} </Typography>
          <span
            className={styles.text}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: promotionbar_after_text }}
          />
        </div>
      </div>
    );
  };

  const mainContent = isMobile ? (
    <div className="promotion-bar-slider">
      {renderCartNudge()}
      {slideBlocks.map((block) => (block.type === 'mobile' && (
        <div className="promotion-slide" key={block.settings.promotion_text}>
          {block.settings.promotion_link && !(block.settings.promotion_link_1 || block.settings.promotion_link_2) ? (
            <a className="promotion-link" href={block.settings.promotion_link}>
              <div
                className="promotion-title has-text-centered"
                style={{ color: sectionSettings.promotion_color }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: block.settings.promotion_text }}
              />
            </a>
          ) : (
            <div
              className="promotion-title has-text-centered"
              style={{ color: sectionSettings.promotion_color }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: block.settings.promotion_text }}
            />
          )}
          <div className="links">
            {block.settings.promotion_link_1 && (
              <a
                href={block.settings.promotion_link_1}
                className="slide-inner-link"
                style={{ color: sectionSettings.promotion_color }}
              >
                {block.settings.promotion_link_label_1}
              </a>
            )}
            {block.settings.promotion_link_1 && block.settings.promotion_link_2 && (
              <span className="spacer" style={{ color: sectionSettings.promotion_color }}>|</span>
            )}
            {block.settings.promotion_link_2 && (
              <a
                href={block.settings.promotion_link_2}
                className="slide-inner-link"
                style={{ color: sectionSettings.promotion_color }}
              >
                {block.settings.promotion_link_label_2}
              </a>
            )}
          </div>
        </div>
      )))}
    </div>
  ) : (
    <div className="promotion-bar-slider">
      {renderCartNudge()}
      {slideBlocks.map((block) => (block.type === 'desktop' && (
        <div
          key={block.settings.promotion_text}
          className={classnames('promotion-slide', {
            'has-tooltip': block.settings.promotion_tooltip
          })}
        >
          {block.settings.promotion_link ? (
            <a className="promotion-link" href={block.settings.promotion_link}>
              <div
                className="promotion-title has-text-centered"
                style={{ color: sectionSettings.promotion_color }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: block.settings.promotion_text }}
              />
            </a>
          ) : (
            <div
              className="promotion-title has-text-centered"
              style={{ color: sectionSettings.promotion_color }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: block.settings.promotion_text }}
            />
          )}
          {block.settings.promotion_tooltip && (<div className="ck-tooltip">{block.settings.promotion_tooltip}</div>)}
        </div>
      )))}
    </div>
  );

  // Timer
  const currentTime = new Date();
  const startTime = parseSqlDate(sectionSettings.countdown_starttime, 'AU');
  const endTime = parseSqlDate(sectionSettings.countdown_endtime, 'AU');
  const countdownActive = startTime <= currentTime && startTime < endTime && endTime > currentTime;
  const countdownTimer = (
    <div className={styles.countdown}>
      <div className={styles.countdownTitle}>
        {sectionSettings.countdown_link ? (
          <a href={sectionSettings.countdown_link}>
            {sectionSettings.countdown_title}
          </a>
        ) : (
          sectionSettings.countdown_title
        )}
      </div>
      <div className={styles.countdownBlock}>
        <FlipTimer
          className={styles.countdownTimer}
          endTime={sectionSettings.countdown_endtime}
          backgroundColor={sectionSettings.promotion_background}
        />
      </div>
    </div>
  );

  return (
    <div
      className={styles.outer}
      style={{
        backgroundColor: sectionSettings.promotion_background,
        color: sectionSettings.promotion_color
      }}
    >
      <div className={classnames(styles.container, 'container')}>
        <div className={styles.parent}>
          {sectionSettings.promotion_button_enable_left && renderBtn('left')}
          <div className={classnames('is-relative', styles.containerMain)}>
            {sectionSettings.countdown_enabled && countdownActive ? countdownTimer : mainContent}
          </div>
          {sectionSettings.promotion_button_enable_right && renderBtn('right')}
        </div>
      </div>
    </div>
  );
};

PromotionBar.propTypes = {
  sectionSettings: PropTypes.shape({
    promotion_background: PropTypes.string,
    promotion_color: PropTypes.string,
    promotion_bar_nudge_enable: PropTypes.bool,
    promotion_button_enable_left: PropTypes.bool,
    promotion_button_text_left: PropTypes.string,
    promotion_button_link_left: PropTypes.string,
    promotion_button_enable_right: PropTypes.bool,
    promotion_button_text_right: PropTypes.string,
    promotion_button_link_right: PropTypes.string,
    countdown_enabled: PropTypes.bool,
    countdown_title: PropTypes.string,
    countdown_link: PropTypes.string,
    countdown_starttime: PropTypes.string,
    countdown_endtime: PropTypes.string,
    slider_auto_play_ms: PropTypes.string
  }),
  slideBlocks: PropTypes.array.isRequired,
  nudgeBlocks: PropTypes.array.isRequired
};

PromotionBar.defaultProps = {
  sectionSettings: {}
};

export { PromotionBar };
